<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Label Types</p>
    </header>
    <section class="modal-card-body">
      <b-tabs v-model="activeTab" :animated="false">
        <b-tab-item label="Modify">
          <b-field label="Name">
            <b-input v-model="name"></b-input>
          </b-field>
          <b-button
            type="is-info"
            @click="addLabelType"
            :disabled="isAddDisabled"
            >Add</b-button
          >
          <div class="spacer"></div>
          <b-field>
            <div class="mlist">
              <div
                v-for="(o, i) in labelTypes"
                :key="i"
                class="mitem"
                :style="labelToStyle(o)"
                @click="
                  () => {
                    selectedLabelType = o.name;
                  }
                "
              >
                {{ o.name }}
              </div>
            </div>
          </b-field>
          <div class="row">
            <div class="right">
              <b-button
                type="is-warning"
                @click="removeLabelType"
                :disabled="isRemoveDisabled"
                >Remove</b-button
              >
            </div>
            <div class="left">
              <b-button type="is-light" @click="assignRandomColors"
                >Randomize Colors</b-button
              >
            </div>
          </div>
        </b-tab-item>
        <b-tab-item label="Sync">
          <b-table
            :columns="[{ field: 'name', label: 'Name' }]"
            :data="tableData"
            paginated
            :per-page="10"
            :current-page.sync="currentPage"
            checkable
            :checked-rows.sync="checkedRows"
          >
          </b-table>
          <div class="row">
            <b-button type="is-info" @click="syncToOtherProjects"
              >Apply to other projects</b-button
            >
          </div>
        </b-tab-item>
      </b-tabs>
    </section>
    <footer class="modal-card-foot"></footer>
  </div>
</template>

<script>
import { computed, ref, onMounted } from "@vue/composition-api";
import { SnackbarProgrammatic as Snackbar } from "buefy";
import randomColor from "randomcolor";
import ui from "@/store/modules/ui";
import $axios from "@/axios-instance";

export default {
  setup() {
    // Ui states
    const name = ref("");
    const selectedLabelType = ref("");
    const isAddDisabled = computed(() => name.value === "");
    const isRemoveDisabled = computed(() => selectedLabelType.value === "");
    const activeTab = ref(0);
    // Table states
    const tableData = ref([]);
    const currentPage = ref(1);
    const checkedRows = ref([]);

    // label types
    const labelTypes = computed(() => ui.labelTypes);
    const labelToStyle = labelObject => {
      const ret = {};

      if (labelObject.name === selectedLabelType.value) {
        ret["background"] = "#dbdbdb";
      }
      return {
        ...ret,
        borderBottom: `0.5rem solid ${labelObject.bgColor}`
      };
    };

    const addLabelType = async () => {
      // If the user tries to add a duplicate, abort
      const duplicate = labelTypes.value.find(
        label => label.name === name.value
      );
      if (duplicate) {
        Snackbar.open({
          message: "Label with the same name already exists",
          actionText: "Close",
          type: "is-warning",
          indefinite: true,
          position: "is-top"
        });
        return;
      }
      // Append new label type with a random color to label types
      const newLabelTypes = labelTypes.value.concat([
        {
          name: name.value,
          fgColor: "#fff",
          bgColor: randomColor()
        }
      ]);
      // Set new label types to the store
      ui.setLabelTypes(newLabelTypes);
      // Sync with server
      await ui.postLabelTypes();
      await ui.fetchLabelTypes();
      // Reset form
      name.value = "";
    };

    const removeLabelType = async () => {
      // Abort if nothing is selected
      if (selectedLabelType.value === "") return;
      // Create new label types filtering out selectd label type
      const newLabelTypes = labelTypes.value.filter(
        label => label.name !== selectedLabelType.value
      );
      // Set to local store and sync with server
      ui.setLabelTypes(newLabelTypes);
      await ui.postLabelTypes();
      await ui.fetchLabelTypes();
      // Reset ui
      selectedLabelType.value = "";
    };

    const assignRandomColors = async () => {
      const newLabelTypes = labelTypes.value.map(label => ({
        name: label.name,
        fgColor: "#fff",
        bgColor: randomColor()
      }));

      ui.setLabelTypes(newLabelTypes);
      await ui.postLabelTypes();
      await ui.fetchLabelTypes();
    };

    const syncToOtherProjects = async () => {
      try {
        await Promise.all(
          checkedRows.value.map(async nameObject => {
            const { name } = nameObject;
            await $axios.put(`/projects/${name}`, {
              labelTypes: ui.labelTypes
            });
          })
        );
        checkedRows.value = [];
      } catch (err) {
        console.error(err);
        Snackbar.open({
          message: "Server access failed",
          actionText: "Close",
          type: "is-warning",
          indefinite: true,
          position: "is-top"
        });
      }
    };

    // Hooks
    // ---------------------------------------------------------------
    onMounted(async () => {
      const result = await $axios.get("/projects");
      if (result.data.success) {
        tableData.value = result.data.payload.map(o => ({
          name: o
        }));
      }
    });

    return {
      name,
      tableData,
      currentPage,
      checkedRows,
      selectedLabelType,
      activeTab,
      labelTypes,
      labelToStyle,
      isAddDisabled,
      isRemoveDisabled,
      addLabelType,
      removeLabelType,
      assignRandomColors,
      syncToOtherProjects
    };
  }
};
</script>

<style lang="scss" scoped>
.mlist {
  border: solid 1px #dbdbdb;
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
}

.mitem {
  flex: 0 50%;
  padding: 0.2rem;
  height: 2.5rem;
  line-height: 1.7rem;
  border-bottom: 1px solid #ddbbdd;
  text-align: left;
  padding-left: 1rem;
  cursor: pointer;

  &:last-child {
    border: none;
  }
}

.spacer {
  height: 1rem;
}

.left {
  display: flex;
  flex-direction: row;
}

.right {
  display: flex;
  flex-direction: row-reverse;
}

.row {
  display: flex;
  justify-content: space-between;
}
</style>
